import React, { Dispatch, useState } from 'react';
import { IService } from 'interfaces/services';
import { useServiceSidebar } from '../context/serviceSidebarContext';
import ServiceItem from './Item/ServiceItem';
import { PRACTICE_FILTER_OPTIONS, SERVICE_NAME } from 'utils/constants';
import SidePanel from './SidePanel/SidePanel';
import { ReactComponent as TriangleIcon } from 'assets/icons/triangle.svg';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { reorder } from 'utils/common';
import axiosInstance from 'apis/axiosInstance';
import { renderToast } from 'components/Toast/Toast';
import { BAD_REQUEST } from 'utils/constants/statusCode';
import { loadSelectedClinicId } from 'utils/storage';

const { ALL, ACTIVE } = PRACTICE_FILTER_OPTIONS;

interface IProps {
  services: IService[];
  selectedService: IService;
  setSelectedService: Dispatch<React.SetStateAction<IService | null>>;
  totalServiceByStatus: {
    active: number;
    inactive: number;
    all: number;
  } | null;
  selectedFilter: string;
  setSelectedFilter: (status: string) => void;
  isExamCleaningExpanded: boolean;
  setIsExamCleaningExpanded: Dispatch<React.SetStateAction<boolean>>;
}

const ServicePanel = ({
  services,
  setSelectedService,
  selectedService,
  totalServiceByStatus,
  selectedFilter,
  setSelectedFilter,
  isExamCleaningExpanded,
  setIsExamCleaningExpanded,
}: IProps) => {
  const [isOrdering, setIsOrdering] = useState(false);

  const { isServiceCollapsed, setIsServiceCollapsed } = useServiceSidebar();

  const getFilterService = (filterOption: string) => {
    const filterServices =
      filterOption !== ALL
        ? services.filter((service) => {
            if (filterOption === ACTIVE) {
              return service.isActive;
            }
            return !service.isActive;
          })
        : services;
    return filterServices;
  };

  const [filteredServices, setFilteredServices] = useState(() =>
    getFilterService(selectedFilter)
  );

  const handleSelectFilter = (option: string) => {
    const filtered = getFilterService(option);
    const firstService = filtered[0] || null;

    setSelectedFilter(option);
    if (firstService) {
      setSelectedService(firstService);
    }
  };

  const isOptionDropdownDisabled = (option: string) => {
    return (
      totalServiceByStatus?.[
        option.toLowerCase() as keyof typeof totalServiceByStatus
      ] === 0
    );
  };

  const onSelectService = (item: IService) => {
    if (isOrdering) return;
    setSelectedService(item);
  };

  const firstInactiveItem = filteredServices.find((item) => !item.isActive);

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (!destination || !services) return;
    if (destination.index === source.index) return;

    const items = reorder(filteredServices, source.index, destination.index);

    setFilteredServices(items);
  };

  const onMoveUp = (currentIndex: number) => {
    if (currentIndex <= 0) return;
    const items = reorder(filteredServices, currentIndex, currentIndex - 1);
    setFilteredServices(items);
  };

  const onMoveDown = (currentIndex: number) => {
    if (currentIndex >= filteredServices.length - 1) return;

    const currentItem = filteredServices[currentIndex];
    const nextItem = filteredServices[currentIndex + 1];

    if (currentItem.isActive && !nextItem.isActive) return;

    const items = reorder(filteredServices, currentIndex, currentIndex + 1);
    setFilteredServices(items);
  };

  const onOrdering = () => {
    setIsOrdering((prev) => !prev);
    setIsExamCleaningExpanded(false);
  };

  const onDoneOrdering = async () => {
    const clinicId = loadSelectedClinicId();
    try {
      await axiosInstance.patch(`/clinics/${clinicId}/services/order`, {
        ordersDefaultService: filteredServices.map((service, index) => ({
          serviceId: service.id,
          order: index + 1,
        })),
      });
      renderToast({
        type: 'success',
        message: 'Updated order successfully',
      });
      setIsOrdering(false);
    } catch (error: any) {
      let message = 'Something went wrong. Please try again later';
      if (error.response?.status === BAD_REQUEST) {
        message = error.response?.data.message;
      }
      renderToast({
        type: 'error',
        message,
      });
    }
  };

  return (
    <SidePanel
      isCollapsed={isServiceCollapsed}
      onCollapsedIconClicked={() => {
        setIsServiceCollapsed(!isServiceCollapsed);
      }}
      selectedFilter={selectedFilter}
      onFilterOptionClicked={(option) => handleSelectFilter(option)}
      isOptionDropdownDisabled={isOptionDropdownDisabled}
      numberOfFilters={filteredServices.length}
      options={PRACTICE_FILTER_OPTIONS}
      isOrdering={isOrdering}
      onOrdering={onOrdering}
      onDoneOrdering={onDoneOrdering}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="service-panel-droppable">
          {(droppableProvided, droppableStateSnapshot) => (
            <div
              {...droppableProvided.droppableProps}
              ref={droppableProvided.innerRef}
            >
              {filteredServices.map((item: IService, index: number) => {
                const isDraggableService = isOrdering && item.isActive;

                if (!isDraggableService) {
                  return (
                    <React.Fragment key={item.id}>
                      <div
                        className={`${
                          droppableStateSnapshot.isDraggingOver
                            ? ''
                            : 'hover:opacity-90'
                        } ${
                          isOrdering &&
                          firstInactiveItem &&
                          firstInactiveItem.id === item.id
                            ? 'border-t-2 border-t-grey'
                            : ''
                        }`}
                      >
                        <ServiceItem
                          service={item}
                          onClick={() => onSelectService(item)}
                          selectedServiceId={selectedService?.id}
                          isCollapsed={isServiceCollapsed}
                          header={
                            item.name === SERVICE_NAME.EXAM_CLEANING && (
                              <button
                                className="text-16 leading-[2.4rem] font-bold flex items-center gap-x-0.5 text-left"
                                onClick={() =>
                                  setIsExamCleaningExpanded(
                                    !isExamCleaningExpanded
                                  )
                                }
                              >
                                <TriangleIcon
                                  className={`w-1.4 h-1.4 inline-block ${isExamCleaningExpanded ? '' : '-rotate-90'} shrink-0`}
                                />
                                <span>
                                  {item.displayName
                                    ? item.displayName
                                    : item.name}
                                </span>
                              </button>
                            )
                          }
                        />
                      </div>
                      {item.childServices.length > 0 &&
                        isExamCleaningExpanded &&
                        item.childServices.map((childService) => (
                          <ServiceItem
                            key={`${childService.id}`}
                            service={childService}
                            onClick={() => onSelectService(childService)}
                            selectedServiceId={selectedService?.id}
                            isCollapsed={isServiceCollapsed}
                          />
                        ))}
                    </React.Fragment>
                  );
                }

                return (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(provided, snapshot) => (
                      <>
                        <div
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          ref={provided.innerRef}
                          style={{
                            ...provided.draggableProps.style,
                          }}
                          onMouseDown={(e) => {
                            e.currentTarget.style.cursor = 'grabbing';
                          }}
                          onMouseUp={(e) => {
                            e.currentTarget.style.cursor = 'grab';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.cursor = 'grab';
                          }}
                          className={`${
                            snapshot.isDragging ? 'shadow-elevation-08' : ''
                          }`}
                        >
                          <ServiceItem
                            service={item}
                            onClick={() => onSelectService(item)}
                            selectedServiceId={selectedService?.id}
                            isCollapsed={isServiceCollapsed}
                            isOrdering={isOrdering}
                            currentIndex={index}
                            onMoveDown={onMoveDown}
                            onMoveUp={onMoveUp}
                            totalItems={filteredServices.length}
                            nextItemIsInactive={
                              index + 1 < filteredServices.length &&
                              !filteredServices[index + 1].isActive
                            }
                            header={
                              !isOrdering &&
                              item.name === SERVICE_NAME.EXAM_CLEANING && (
                                <button
                                  className="text-16 leading-[2.4rem] font-bold flex items-center gap-x-0.5"
                                  onClick={() =>
                                    setIsExamCleaningExpanded(
                                      !isExamCleaningExpanded
                                    )
                                  }
                                >
                                  <TriangleIcon
                                    className={`w-1.4 h-1.4 inline-block ${isExamCleaningExpanded ? '' : '-rotate-90'}`}
                                  />
                                  <span>{item.name}</span>
                                </button>
                              )
                            }
                          />
                        </div>
                        {!isOrdering &&
                          item.childServices.length > 0 &&
                          isExamCleaningExpanded &&
                          item.childServices.map((childService) => (
                            <ServiceItem
                              key={`${childService.id}`}
                              service={childService}
                              onClick={() => onSelectService(childService)}
                              selectedServiceId={selectedService?.id}
                              isCollapsed={isServiceCollapsed}
                            />
                          ))}
                        {firstInactiveItem &&
                          index + 1 < filteredServices.length &&
                          filteredServices[index + 1].id ===
                            firstInactiveItem.id &&
                          droppableProvided.placeholder}
                      </>
                    )}
                  </Draggable>
                );
              })}
              {!firstInactiveItem && droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </SidePanel>
  );
};

export default ServicePanel;
